import React, { useState } from 'react';
import { getToday } from '@shared/services/dateService';
import WeeklyDatePicker from '../WeeklyCalendar/WeeklyDatePicker';
import ApolloError from '@shared/components/ApolloError';
import WeeklyCalendar from '../WeeklyCalendar/WeeklyCalendar';
import CalendarEventMapper from '../../mappers/CalendarEventMapper';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import moment from 'moment';
import { useQuery, useReactiveVar } from '@apollo/client';
import PrivilegeNames from '../../../../constants/privilegeNames';
import { userPrivileges } from '@apollo-internal/cache';
import AvailabilityCheckbox from '../shared/AvailabilityCheckbox';
import useAvailability from '../../hooks/useAvailability';
import MultiItemUpDown from '@shared/components/MultiItemUpDown';
import GET_ATM_USERS from '../../queries/getAtmUsersQuery';
import _ from 'lodash';
import ViewSwitch from '../shared/components/ViewSwitch.js';
import { useIsUserActingAsATMStore, useSelectedDateStore } from '../../CalendarStore';
import { ClickableLegends } from '../shared/components/clickableLegends';
import { showErrorToast } from '@shared/services/toastService';
import { useGetATMCalendarQuery } from '../shared/hooks/useGetATMCalendarQuery';
import { useShallow } from 'zustand/shallow';

const UNCHECKED_LABEL = 'ATM';
const NO_EVENTS_MESSAGE = 'No matching results.';

const ALL_UNITS_OPTION = { key: -1, value: 'Unit(s)' };

const ATMCalendar = () => {
  const [selectedLegend, setSelectedLegend] = useState(null);

  const [selectedDate, setSelectedDate, resetSelectedDate] = useSelectedDateStore(
    useShallow(state => [state.selectedDate, state.setSelectedDate, state.resetSelectedDate])
  );

  const isUserActingAsATM = useIsUserActingAsATMStore(useShallow(state => state.isUserActingAsATM));
  const today = getToday();

  const privileges = useReactiveVar(userPrivileges);

  const {
    cantAccessPastEvents,
    canUpdateUserAvailability,
    canAccessUnitAvailability,
    canAccessAtmUsersCalendar
  } = getUserAccessPrivilege(privileges);

  const [selectedUser, setSelectedUser] = useState(
    canAccessAtmUsersCalendar ? ALL_UNITS_OPTION.key : null
  );

  const setSelectedUserAndGetCloset = value => {
    setSelectedUser(value);
    resetSelectedDate();
  };

  const isSelectedAtm = selectedUser !== ALL_UNITS_OPTION.key;
  const hasAvailabilityCheckBox =
    isSelectedAtm || canUpdateUserAvailability || canAccessUnitAvailability;
  const selectedExternalUserId = isSelectedAtm ? selectedUser : null;

  const isLeftArrowDisabled =
    cantAccessPastEvents && moment(today).isSameOrAfter(moment(selectedDate).startOf('week'));
  const isSelectedWeekInThePast = moment(today).isSameOrAfter(moment(selectedDate).endOf('week'));

  const {
    data: calendarData,
    loading: calendarDataLoading,
    error: calendarDataError
  } = useGetATMCalendarQuery({
    variables: {
      externalUserId: selectedExternalUserId,
      eventPhase: selectedLegend
    },
    onCompleted: data => {
      if (data?.hasAppraiserEvents === false) {
        showErrorToast(NO_EVENTS_MESSAGE);
      }
    }
  });

  const {
    availability,
    isUserAssignedToEventWithinPeriod,
    error: availabilityDataError,
    loading: availabilityDataLoading,
    backgroundLoading: availabilityBackgroundLoading,
    handleAvailabilityChange
  } = useAvailability(selectedDate, selectedExternalUserId);

  const {
    data: atmUsersData,
    loading: atmUsersLoading,
    error: atmUsersError
  } = useQuery(GET_ATM_USERS, {
    skip: !canAccessAtmUsersCalendar
  });

  const appraiserEvents = calendarData
    ? CalendarEventMapper.toCalendarEventData(calendarData.appraiserEvents)
    : [];

  const atmUsers =
    _.sortBy(atmUsersData?.orgAtmUsers, [
      user => user.CurrentRoleName,
      user => user.Name.toLowerCase()
    ]).map(user => ({
      value: `${user.Name} - ${user.CurrentRoleName}`,
      key: user.ExternalUserId
    })) || [];

  if (calendarDataError || availabilityDataError || atmUsersError) {
    return <ApolloError />;
  }

  return (
    <div className='calendar-view'>
      <div className='calendar-header'>
        <WeeklyDatePicker
          loading={calendarDataLoading}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          isLeftArrowDisabled={isLeftArrowDisabled}
          minDate={cantAccessPastEvents ? today : null}
        />

        {canAccessAtmUsersCalendar && (
          <MultiItemUpDown
            id='select-users'
            className='users-list'
            options={[ALL_UNITS_OPTION, ...atmUsers]}
            value={selectedUser}
            setValue={setSelectedUserAndGetCloset}
            loading={atmUsersLoading}
            selectId='selected-users'
          />
        )}

        {hasAvailabilityCheckBox && (
          <SkeletonWrapper show={availabilityDataLoading}>
            <AvailabilityCheckbox
              checked={availability && !isUserAssignedToEventWithinPeriod}
              disabled={
                availabilityBackgroundLoading ||
                isSelectedWeekInThePast ||
                isUserAssignedToEventWithinPeriod
              }
              onChange={handleAvailabilityChange}
              testId='availability-checkbox'
            />
          </SkeletonWrapper>
        )}

        {isUserActingAsATM && <ViewSwitch activeLabel={UNCHECKED_LABEL} />}

        <ClickableLegends selectedLegend={selectedLegend} setSelectedLegend={setSelectedLegend} />
      </div>

      <div className='calendar-container'>
        <div className='calendar-body'>
          <SkeletonWrapper inheritParentSize inheritChildHeight show={calendarDataLoading}>
            <WeeklyCalendar
              selectedAtmUser={canAccessAtmUsersCalendar ? selectedExternalUserId : undefined}
              selectedDate={selectedDate}
              events={appraiserEvents}
              isWeekUnavailableForOrgUnit={!availability}
              cantAccessPastEvents={cantAccessPastEvents}
              hideBidWarnings={selectedUser === ALL_UNITS_OPTION.key}
              checkAtmAccessibility={isSelectedAtm}
            />
          </SkeletonWrapper>
        </div>
      </div>
    </div>
  );
};

const getUserAccessPrivilege = privileges => {
  const cantAccessPastEvents = !privileges.includes(PrivilegeNames.Calendar_AccessPastEvents);
  const canAccessUnitAvailability = privileges.includes(
    PrivilegeNames.Calendar_ViewEvents_UnitAvailability
  );
  const canAccessAtmUsersCalendar = privileges.includes(
    PrivilegeNames.Calendar_ViewEvents_AtmUsers
  );
  const canUpdateUserAvailability = privileges.includes(
    PrivilegeNames.Calendar_ViewEvents_UserAvailability
  );

  return {
    cantAccessPastEvents,
    canUpdateUserAvailability,
    canAccessUnitAvailability,
    canAccessAtmUsersCalendar
  };
};

export default ATMCalendar;
